// src/store/slices/userSlice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Profile } from '../../remote/types/responses';
import { loadFromStorage, saveToStorage, removeFromStorage, STORAGE_KEYS, saveByEncrypt, loadByDecrypt } from '../../utils/storage';

interface UserState extends Profile {
    isLogin: boolean;
    isMember: boolean;
    memberExpire: string; // ISO 8601 格式. 示例：2023-12-31T23:59:59Z
    memberType: number; // 会员类型：0-非会员，1-季度会员，2-年度会员
    loading: boolean;
}

const defaultState: UserState = {
    userId: '',
    email: '',
    nickname: '',
    company: '',
    position: '',
    avatar: '',
    isLogin: false,
    isMember: false,
    memberExpire: '',
    memberType: 0,
    loading: false,
};

const storedState = loadByDecrypt(STORAGE_KEYS.USER);
let initialState: UserState = storedState || defaultState;

// Check membership status on initial load
if (storedState?.memberExpire) {
    const now = new Date();
    const expireDate = new Date(storedState.memberExpire);
    if (now > expireDate) {
        initialState = {
            ...storedState,
            isMember: false,
            memberExpire: ''
        };
        saveByEncrypt(STORAGE_KEYS.USER, initialState);
    }
}

const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        setUser: (state, action: PayloadAction<Partial<UserState>>) => {
            const newState = { ...state, ...action.payload };
            saveByEncrypt(STORAGE_KEYS.USER, newState);
            return newState;
        },
        checkMemberExpiration: (state) => {
            const now = new Date();
            const expireDate = state.memberExpire ? new Date(state.memberExpire) : null;
            
            if (expireDate && now > expireDate) {
                state.isMember = false;
                state.memberExpire = '';
                state.memberType = 0;
                saveByEncrypt(STORAGE_KEYS.USER, state);
            }
        },
        logout: () => {
            removeFromStorage(STORAGE_KEYS.USER);
            return defaultState;
        },
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload;
        },
    },
});

export const { setUser, checkMemberExpiration, logout, setLoading } = userSlice.actions;
export default userSlice.reducer;